import React, { useCallback } from "react";
import {
  FaTriangleExclamation,
  FaWifi,
} from "react-icons/fa6";
import Button from "react-bootstrap/Button";
import Badge from "react-bootstrap/Badge";
import Alert from "react-bootstrap/Alert";
import Spinner from "react-bootstrap/Spinner";
import { MdRefresh } from "react-icons/md";
import Image from "react-bootstrap/Image";
import axios from "axios";
import GLOBALS from "../../utils/Globals";

// assets
import SFIcon from "../../assets/img/Salesforce.com_logo.svg.png";
import SAPIcon from "../../assets/img/SAP.png";
import OracleIcon from "../../assets/img/images.png";
import MicrosoftIcon from "../../assets/img/Dynamics-365-logo.png";
import ShopifyIcon from "../../assets/img/Shopify-Logo.png";
import AdobeIcon from "../../assets/img/adobe-icon.webp";
import WSIcon from "../../assets/img/ws.png";
import SnowflakeIcon from "../../assets/img/SNO-SnowflakeLogo_blue.png";
import JDBCIcon from "../../assets/img/JDBC-icon-for-blog.jpg";
import ServiceNowIcon from "../../assets/img/servicenow_logo_icon_168837.png";
import HubspotIcon from "../../assets/img/hubspot.svg";
import WorkdayIcon from "../../assets/img/workday.svg";
import PeopleSoftIcon from "../../assets/img/PeopleSoft-logo.webp";
import SQLServerIcon from "../../assets/img/sqlserver.png";
import MySQLIcon from "../../assets/img/mysql.png";
import Footer from "../Footer/Footer";

const Connections = () => {
  const [connections, setConnections] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const baseURLGetConnections =
    process.env.REACT_APP_ENV === "PROD"
      ? GLOBALS.PROD.connections
      : GLOBALS.DEV.connections;

  const loadConnections = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(baseURLGetConnections);
      setConnections(response.data.data);
    } catch (err) {
      console.error('Error loading connections:', err);

      // Handle different types of errors
      let errorMessage = 'Failed to load connections. Please try again.';
      let errorType = 'general';

      if (err.code === 'ERR_NETWORK' || err.message.includes('ERR_CONNECTION_REFUSED')) {
        errorMessage = 'Unable to connect to the server. Please check your internet connection and try again.';
        errorType = 'connection';
      } else if (err.code === 'ECONNREFUSED' || err.message.includes('ECONNREFUSED')) {
        errorMessage = 'The server is currently unavailable. Please try again in a few moments.';
        errorType = 'connection';
      } else if (err.response?.status === 404) {
        errorMessage = 'Connections service not found. Please contact support.';
        errorType = 'notfound';
      } else if (err.response?.status >= 500) {
        errorMessage = 'Server error occurred. Please try again later.';
        errorType = 'server';
      } else if (err.response?.status === 401 || err.response?.status === 403) {
        errorMessage = 'Access denied. Please check your permissions.';
        errorType = 'auth';
      }

      setError({ message: errorMessage, type: errorType, originalError: err });
    } finally {
      setLoading(false);
    }
  }, [baseURLGetConnections]);

  React.useEffect(() => {
    loadConnections();
  }, [loadConnections]);

  // Image mapping object for cleaner rendering
  const getConnectionImage = (connection) => {
    const imageMap = {
      sap: SAPIcon,
      salesforce: SFIcon,
      d365: MicrosoftIcon,
      oracle: OracleIcon,
      shopify: ShopifyIcon,
      adobe: AdobeIcon,
      ws: WSIcon,
      snowflake: SnowflakeIcon,
      jdbc: JDBCIcon,
      mysql: MySQLIcon,
      sqlserver: SQLServerIcon,
      hubspot: HubspotIcon,
      peoplesoft: PeopleSoftIcon,
      workday: WorkdayIcon,
      servicenow: ServiceNowIcon
    };
    return imageMap[connection];
  };

  // Error state
  if (error) {
    return (
      <div className="py-5 bg-light">
        <div className="container">
          <div className="mb-4">
            <h1 className="h3 fw-semibold mb-2">Available Connections</h1>
            <p className="text-muted mb-0" style={{ fontSize: "0.9rem" }}>
              Connect your products to leading ERP platforms
            </p>
          </div>

          <Alert variant="danger" className="d-flex align-items-center">
            <FaTriangleExclamation className="me-3" size={24} />
            <div className="flex-grow-1">
              <h5 className="alert-heading mb-2">Connection Error</h5>
              <p className="mb-2">{error.message}</p>
              {error.type === 'connection' && (
                <div className="small text-muted mb-3">
                  <FaWifi className="me-1" />
                  This usually indicates a network connectivity issue or the server is temporarily unavailable.
                </div>
              )}
              <Button
                variant="outline-danger"
                size="sm"
                onClick={loadConnections}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Retrying...
                  </>
                ) : (
                  <>
                    <MdRefresh className="me-1" />
                    Try Again
                  </>
                )}
              </Button>
            </div>
          </Alert>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="py-5 bg-light">
        <div className="container">
          <div className="mb-4">
            <h1 className="h3 fw-semibold mb-2">Available Connections</h1>
            <p className="text-muted mb-0" style={{ fontSize: "0.9rem" }}>
              Connect your products to leading ERP platforms
            </p>
          </div>

          <div className="text-center py-5">
            <Spinner animation="border" role="status" className="mb-3">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="text-muted">Loading available connections...</p>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (!connections || connections.length === 0) {
    return (
      <div className="py-5 bg-light">
        <div className="container">
          <div className="mb-4">
            <h1 className="h3 fw-semibold mb-2">Available Connections</h1>
            <p className="text-muted mb-0" style={{ fontSize: "0.9rem" }}>
              Connect your products to leading ERP platforms
            </p>
          </div>

          <Alert variant="info" className="text-center">
            <h5>No Connections Available</h5>
            <p className="mb-0">There are currently no connection options available. Please check back later.</p>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="py-5 bg-light">
      <div className="container">
        <div className="mb-4">
        <h1 className="h3 fw-semibold mb-2">Available Connections</h1>
        <p className="text-muted mb-0" style={{ fontSize: "0.9rem" }}>
          Connect your products to leading ERP platforms
        </p>
        </div>

        <div className="row g-4">
          {connections.map((service, index) => (
            <div key={index} className="col-lg-3 col-md-4 col-sm-6">
              <div
                className="card h-100 shadow-sm"
                style={{ transition: "transform 0.3s" }}
                onMouseOver={(e) =>
                  (e.currentTarget.style.transform = "scale(1.02)")
                }
                onMouseOut={(e) =>
                  (e.currentTarget.style.transform = "scale(1)")
                }
              >
                <div className="card-body d-flex flex-column">
                  <div
                    className="text-center mb-4"
                    style={{
                      height: "120px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Image
                      src={getConnectionImage(service.connection)}
                      style={{
                        maxWidth: "120px",
                        maxHeight: "120px",
                        objectFit: "contain",
                      }}
                      alt={service.title}
                    />
                  </div>

                  <h5 className="card-title text-center mb-2">
                    {service.title}
                  </h5>
                  <div className="text-center mb-3">
                    <Badge bg="success">Available</Badge>
                  </div>
                  <p className="card-text text-muted flex-grow-1">
                    {service.description}
                  </p>

                  <div className="text-center mt-3">
                    <Button
                      variant="outline-primary"
                      disabled={true}
                      className="w-100"
                    >
                      Request
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Connections;


///////// VARIANT 2

// import React, { useCallback } from "react";
// import {
//   FaCartPlus,
//   FaBriefcaseMedical,
//   FaHeadset,
//   FaUserTie,
//   FaUtensils,
//   FaDatabase,
// } from "react-icons/fa6";
// import { PiPlugsConnectedFill } from "react-icons/pi";
// import { CiImport } from "react-icons/ci";
// import { Link } from "react-router-dom";
// import Button from "react-bootstrap/Button";
// import Badge from "react-bootstrap/Badge";
// import { GrValidate } from "react-icons/gr";
// import { MdDelete } from "react-icons/md";
// import Image from "react-bootstrap/Image";
// import axios from "axios";
// import GLOBALS from "../../utils/Globals";

// assets
// import SFIcon from "../../assets/img/Salesforce.com_logo.svg.png";
// import SAPIcon from "../../assets/img/SAP.png";
// import OracleIcon from "../../assets/img/images.png";
// import MicrosoftIcon from "../../assets/img/Dynamics-365-logo.png";
// import ShopifyIcon from "../../assets/img/Shopify-Logo.png";
// import AdobeIcon from "../../assets/img/adobe-icon.webp";
// import WSIcon from "../../assets/img/ws.png";
// import SnowflakeIcon from "../../assets/img/SNO-SnowflakeLogo_blue.png";
// import JDBCIcon from "../../assets/img/JDBC-icon-for-blog.jpg";
// import ServiceNowIcon from "../../assets/img/servicenow_logo_icon_168837.png";
// import HubspotIcon from "../../assets/img/hubspot.svg";
// import WorkdayIcon from "../../assets/img/workday.svg";
// import PeopleSoftIcon from "../../assets/img/PeopleSoft-logo.webp";
// import SQLServerIcon from "../../assets/img/sqlserver.png";
// import MySQLIcon from "../../assets/img/mysql.png";

// const Connections = () => {
//   const [connections, setConnections] = React.useState(null);

//   const baseURLGetConnections =
//     process.env.REACT_APP_ENV === "PROD"
//       ? GLOBALS.PROD.connections
//       : GLOBALS.DEV.connections;

//   React.useEffect(() => {
//     const loadConnections = async () => {
//       const response = await axios.get(baseURLGetConnections);
//       setConnections(response.data.data);
//     };
//     loadConnections();
//   }, []);

//   const getConnectionImage = (connection) => {
//     const imageMap = {
//       sap: SAPIcon,
//       salesforce: SFIcon,
//       d365: MicrosoftIcon,
//       oracle: OracleIcon,
//       shopify: ShopifyIcon,
//       adobe: AdobeIcon,
//       ws: WSIcon,
//       snowflake: SnowflakeIcon,
//       jdbc: JDBCIcon,
//       mysql: MySQLIcon,
//       sqlserver: SQLServerIcon,
//       hubspot: HubspotIcon,
//       peoplesoft: PeopleSoftIcon,
//       workday: WorkdayIcon,
//       servicenow: ServiceNowIcon
//     };
//     return imageMap[connection];
//   };

//   if (!connections) return null;

//   return (
//     <div className="py-4 bg-light">
//       <div className="container">
//         <div className="mb-4">
//           <h1 className="h3 fw-semibold mb-2">Available Connections</h1>
//           <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
//             Need another connection? Please contact connections@nutrad.io
//           </p>
//         </div>
        
//         <div className="row g-3">
//           {connections.map((service, index) => (
//             <div key={index} className="col-xl-2 col-lg-3 col-md-4 col-sm-6">
//               <div 
//                 className="card h-100 border-0 bg-white" 
//                 style={{ 
//                   transition: 'all 0.2s ease',
//                   borderRadius: '12px',
//                   overflow: 'hidden',
//                 }}
//                 onMouseOver={(e) => {
//                   e.currentTarget.style.transform = 'translateY(-5px)';
//                   e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.08)';
//                 }}
//                 onMouseOut={(e) => {
//                   e.currentTarget.style.transform = 'translateY(0)';
//                   e.currentTarget.style.boxShadow = 'none';
//                 }}
//               >
//                 <div className="card-body p-3 d-flex flex-column">
//                   <div 
//                     className="text-center mb-3" 
//                     style={{ 
//                       height: '80px',
//                       display: 'flex', 
//                       alignItems: 'center', 
//                       justifyContent: 'center',
//                       padding: '8px'
//                     }}
//                   >
//                     <Image
//                       src={getConnectionImage(service.connection)}
//                       style={{
//                         maxWidth: '80px',
//                         maxHeight: '80px',
//                         objectFit: 'contain'
//                       }}
//                       alt={service.title}
//                     />
//                   </div>
                  
//                   <h6 className="card-title text-center mb-2" style={{ fontSize: '0.9rem', fontWeight: '600' }}>
//                     {service.title}
//                   </h6>
                  
//                   <div className="text-center mb-2">
//                     <Badge 
//                       bg="success" 
//                       style={{ 
//                         fontSize: '0.7rem', 
//                         fontWeight: '500',
//                         padding: '0.25em 0.75em'
//                       }}
//                     >
//                       Available
//                     </Badge>
//                   </div>
                  
//                   <p className="card-text text-muted mb-3" 
//                     style={{ 
//                       fontSize: '0.8rem',
//                       lineHeight: '1.4',
//                       flex: '1 1 auto'
//                     }}
//                   >
//                     {service.description}
//                   </p>
                  
//                   <Button 
//                     variant="outline-primary" 
//                     disabled={true}
//                     size="sm"
//                     className="w-100 mt-auto"
//                     style={{ 
//                       fontSize: '0.8rem',
//                       padding: '0.3rem'
//                     }}
//                   >
//                     Request
//                   </Button>
//                 </div>
//               </div>
//             </div>
//           ))}
//         </div>
//       </div>
//     </div>
//   );
// };

// export default Connections;